import React from 'react';
import Dropdown, {Option} from "../../../../ui-kit/src/atoms/Dropdown";
import {BrandsNavbarSettingsDropdownProps, CompanyType} from "./types";
import Button from "../../../../ui-kit/src/atoms/Button";
import styles from "./BrandsNavbarSettingsDropdown.module.scss";
import addClassNames from "../../../../ui-kit/src/classNameUtils";

const BRANDS_DROPDOWN_OPTIONS = {
  "ADD_BRAND": "add_brand",
  "MANAGE_API_KEYS": "manage_api_keys",
  "MANAGE_MEMBERS": "manage_members",
}

function sortCompanies(companies: CompanyType[], currentCompany: CompanyType): CompanyType[] {
  return [...companies].sort((c1, c2) => {
    if (c1.name === currentCompany.name) {
      return -1;
    } else if (c2.name === currentCompany.name) {
      return 1;
    } else {
      return c1.name.localeCompare(c2.name);
    }
  });
}

function companyToOption(company: CompanyType, currentCompany: CompanyType): Option {
  return {
    label: <label className={`mb-0 ${styles.smallFont}`} style={{cursor: "inherit"}}>{company.name}</label>,
    value: company.id,
    searchBase: company.name,
    icon: currentCompany.id === company.id ?
      <i className={`fa-regular fa-circle-dot ${styles.dropdownActiveIcon}`}/> :
      <i className={`fa-regular fa-circle ${styles.dropdownInactiveIcon}`}/>
  };
}

function getCompanyOptions(companies: CompanyType[] | null,
                           currentCompany: CompanyType,
                           companiesStatus: string): Option[] {
  if (companies !== null) {
    return sortCompanies(companies, currentCompany).map(company => companyToOption(company, currentCompany));
  }

  // The list is not available yet: show the active brand right away, followed by the loading / error state
  const options: Option[] = [companyToOption(currentCompany, currentCompany)];
  if (companiesStatus === "error") {
    options.push({
      header: true,
      label: <span className={styles.statusMessage}>Couldn't load your brands. Reopen to retry.</span>,
      icon: <i className={`fa-regular fa-circle-exclamation ${styles.statusIcon}`}/>
    });
  } else {
    options.push({loading: true});
  }
  return options;
}

function actionOption(label: string, value: string, icon: string): Option {
  return {
    label: <label className={`mb-0 ${styles.smallFont}`} style={{cursor: "inherit"}}>{label}</label>,
    value,
    icon: <i className={`fa-regular ${icon} ${styles.dropdownItemIcon}`}/>,
    className: styles.dropdownOptionHover
  };
}

export default function BrandsNavbarSettingsDropdown({
                                                       currentCompany,
                                                       companies,
                                                       companiesStatus = "success",
                                                       onRequestCompanies,
                                                       handleCompanyChange,
                                                       toggleManageApiKeysModal,
                                                       canAddCompany,
                                                       handleAddCompany,
                                                       canChangeBrand,
                                                       canManageMembers,
                                                       handleManageMembers,
                                                     }: BrandsNavbarSettingsDropdownProps) {
  const requestCompanies = () => {
    if (onRequestCompanies) {
      onRequestCompanies();
    }
  };

  const dropdownOptions: Option[] = getCompanyOptions(companies, currentCompany, companiesStatus);

  // The actions stay below the scrollable brand list, so they are always visible
  const footerOptions: Option[] = [];
  if (canManageMembers) {
    footerOptions.push(actionOption("Manage members", BRANDS_DROPDOWN_OPTIONS.MANAGE_MEMBERS, "fa-users"));
  }
  if (canChangeBrand) {
    footerOptions.push(actionOption("Manage API keys", BRANDS_DROPDOWN_OPTIONS.MANAGE_API_KEYS, "fa-key"));
  }
  if (canChangeBrand && canAddCompany) {
    footerOptions.push(actionOption("Add a brand", BRANDS_DROPDOWN_OPTIONS.ADD_BRAND, "fa-circle-plus"));
  }

  return <div className={styles.dropdownContainer}>
    <Dropdown currentValue={null}
              options={dropdownOptions}
              footerOptions={footerOptions}
              onSelectOption={op => {
                if (op.value === BRANDS_DROPDOWN_OPTIONS.ADD_BRAND) {
                  handleAddCompany();
                } else if (op.value === BRANDS_DROPDOWN_OPTIONS.MANAGE_MEMBERS) {
                  handleManageMembers();
                } else if (op.value === BRANDS_DROPDOWN_OPTIONS.MANAGE_API_KEYS) {
                  toggleManageApiKeysModal();
                } else if (op.value && op.value !== currentCompany.id) {
                  handleCompanyChange({id: op.value})
                }
              }}
              ToggleTag={(props) => {
                return <Button onClick={e => {
                                 if (!props.isOpen) {
                                   requestCompanies();
                                 }
                                 props.toggleDropdowns(e);
                               }}
                               aria-haspopup={"menu"}
                               aria-expanded={props.isOpen}
                               className={addClassNames([
                                 {className: styles.dropdownMenuButton, condition: true},
                                 {className: styles.dropdownMenuButtonActive, condition: props.isOpen},
                               ])}
                               color={'transparentSecondary'}>
                  <i className={`fa-regular fa-industry ${styles.userIcon} ${props.isOpen && styles.userIconActive}`}/>
                  <p className={"mb-0 " + styles.smallFont}>
                    {currentCompany.name}
                  </p>
                  <i className={`ml-2 mr-0 fa-solid fa-chevron-down ${styles.chevronIcon}`}/>
                </Button>
              }}/>
  </div>
}
